import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

// ---------------------------------------------------------------------------
// Job header
// ---------------------------------------------------------------------------

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.highlight ? 'white' : props.theme.colors.primary};
`;

// ---------------------------------------------------------------------------
// Two-column layout
// ---------------------------------------------------------------------------

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

// ---------------------------------------------------------------------------
// Left: job details
// ---------------------------------------------------------------------------

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const SubHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.5rem 0 0.6rem;
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TechCard = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid rgba(16,29,64,0.08);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const TechLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
`;

const TechDesc = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Right: sticky apply card
// ---------------------------------------------------------------------------

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.selected ? '600' : '500'};
  opacity: ${props => props.selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success'
    ? '#d4edda'
    : props.type === 'error'
    ? '#f8d7da'
    : '#d1ecf1'};
  color: ${props => props.type === 'success'
    ? '#155724'
    : props.type === 'error'
    ? '#721c24'
    : '#0c5460'};
  border: 1px solid ${props => props.type === 'success'
    ? '#c3e6cb'
    : props.type === 'error'
    ? '#f5c6cb'
    : '#bee5eb'};
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DigitalMarketingInternship = () => {
  const formRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    coverNote: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { type, message }
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!cvFile) {
      setStatus({ type: 'error', message: 'Please attach your CV.' });
      return;
    }
    if (!coverFile) {
      setStatus({ type: 'error', message: 'Please attach your cover letter.' });
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(cvFile.type)) {
      setStatus({ type: 'error', message: 'CV must be a PDF, DOC, or DOCX file.' });
      return;
    }
    if (!allowedTypes.includes(coverFile.type)) {
      setStatus({ type: 'error', message: 'Cover letter must be a PDF, DOC, or DOCX file.' });
      return;
    }

    if (!form.coverNote.trim()) {
      setStatus({ type: 'error', message: 'Please complete the cover note — tell us about yourself.' });
      return;
    }

    setSubmitting(true);
    try {
      const [cvData, coverLetterData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: 'digital-marketing-graduate',
        roleTitle: 'Digital Marketing Assistant — Graduate Role',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        portfolio: form.portfolio,
        coverNote: form.coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
      setStatus({ type: 'success', message: 'Application submitted! We\'ll be in touch.' });
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Graduate: Digital Marketing Assistant"
        subtitle="Belvoir Castle — Historic Estate · Innovation Leader · Digital Frontier"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">Back to Careers</BackLink>

        {/* Job header */}
        <JobHeader>
          <JobSubtitle>Belvoir Castle, Leicestershire</JobSubtitle>
          <JobTitle>Digital Marketing Assistant</JobTitle>

          <MetaRow>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>Belvoir Castle, Leicestershire</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Type</MetaLabel>
              <MetaValue>Graduate role</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Hours</MetaLabel>
              <MetaValue>24–40 hrs/week (flexible)</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Open to</MetaLabel>
              <MetaValue>Recent graduates</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Salary</MetaLabel>
              <MetaValue>Competitive (paid role)</MetaValue>
            </MetaItem>
          </MetaRow>

          <TagRow>
            <Tag highlight>Graduate Role</Tag>
            <Tag highlight>Paid Position</Tag>
            <Tag>Digital Marketing</Tag>
            <Tag>Tech & AI Pathway</Tag>
            <Tag>Permanent Role Opportunity</Tag>
            <Tag>React / Node.js / WordPress</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          {/* Left column — full job spec */}
          <div>
            {/* About */}
            <Section>
              <SectionTitle>About Belvoir Castle</SectionTitle>
              <BodyText>
                Belvoir Castle is the historic stately home of the Duke and Duchess of Rutland, set within a stunning
                Leicestershire estate between Grantham and Melton Mowbray. Our vision is to share the history and
                beauty of the Estate through delivering exceptional experiences — for our local community, visitors
                from across the country, and guests from around the world.
              </BodyText>
              <BodyText>
                We are committed to preserving, growing, and developing the Estate in a sustainable way — honouring
                our past whilst boldly embracing the future. And that future, increasingly, is digital.
              </BodyText>
            </Section>

            {/* About the opportunity */}
            <Section>
              <SectionTitle>About This Opportunity</SectionTitle>
              <BodyText>
                This is a genuine paid graduate role — not a coffee-making internship. From day one, you will be a real
                member of the marketing team, with real responsibility, working across all of Belvoir Castle's
                commercial channels.
              </BodyText>
              <BodyText>
                Belvoir Castle is not just one of England's finest historic estates — we are positioning ourselves as
                a leader in digital innovation in the heritage, hospitality, and events sector. We are investing in
                technology, in mobile, in AI, and in the people who will drive that forward.
              </BodyText>
              <BodyText>
                If you are the right person — curious, driven, technically minded, and hungry to learn — this
                role is a launchpad. Not just into marketing. Into the full digital stack.
              </BodyText>
            </Section>

            {/* Who this is for */}
            <Section>
              <SectionTitle>Who This Is For</SectionTitle>
              <BodyText>
                This role is open to <strong>recent graduates</strong> ready to start their career in marketing and digital.
              </BodyText>
              <BulletList>
                <li>Graduates from Marketing, Digital Media, Computer Science, or related fields who want hands-on experience in a real marketing and digital team</li>
                <li>Career changers or self-taught candidates with demonstrable digital experience — coding, content, campaigns, or personal projects</li>
                <li>Graduates looking for a paid route into marketing and tech, with a genuine pathway to a permanent role for the right person</li>
              </BulletList>
              <BodyText>
                What matters most is your passion, your work ethic, and what you can bring to the team.
              </BodyText>
            </Section>

            {/* Tech stack */}
            <Section>
              <SectionTitle>Our Technology Stack</SectionTitle>
              <BodyText>
                Belvoir Castle runs a modern, full-service digital operation. Here's what powers it — and what you
                will have the opportunity to learn:
              </BodyText>
              <TechGrid>
                <TechCard>
                  <TechLabel>Web & CMS</TechLabel>
                  <TechDesc>WordPress CMS — content management, publishing workflows, plugin ecosystem, SEO optimisation</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Frontend</TechLabel>
                  <TechDesc>React — dynamic web interfaces, interactive booking flows, component-based architecture</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Mobile App</TechLabel>
                  <TechDesc>React Native — cross-platform iOS and Android app for guest experience and estate operations</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Backend & APIs</TechLabel>
                  <TechDesc>Node.js & PHP — server-side logic, API integrations, booking systems, payment processing</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>AI & Automation</TechLabel>
                  <TechDesc>AI-powered content, personalisation, customer insight, bespoke automated offers and operational efficiency</TechDesc>
                </TechCard>
              </TechGrid>
            </Section>

            {/* Responsibilities */}
            <Section>
              <SectionTitle>Responsibilities</SectionTitle>

              <SubHeading>Digital Marketing & Content</SubHeading>
              <BulletList>
                <li>Develop, schedule, and evaluate content across social media channels — Instagram, Facebook, TikTok, LinkedIn, and others</li>
                <li>Build and manage paid digital campaigns (Meta Ads, Google Ads) to drive traffic, awareness, and direct sales</li>
                <li>Manage and grow the Belvoir Castle email database and create campaigns that convert</li>
                <li>Monitor and report on key metrics — reach, engagement, traffic, conversion, database growth</li>
              </BulletList>

              <SubHeading>Strategy & Commercial Growth</SubHeading>
              <BulletList>
                <li>Support the development of a marketing strategy that drives measurable revenue across all business units</li>
                <li>Identify opportunities for new products, improved pricing, and sales initiatives</li>
                <li>Work closely with the Marketing Manager, Digital Manager, and Sales and Revenue teams</li>
                <li>Monitor market trends and competitor activity to keep Belvoir ahead of the curve</li>
              </BulletList>

              <SubHeading>Brand & Reputation</SubHeading>
              <BulletList>
                <li>Act as a guardian of the Belvoir Castle brand — consistent tone, visual identity, and values</li>
                <li>Ensure all communications resonate with our existing audience while attracting new guests</li>
                <li>Support reputation management across review platforms and social channels</li>
              </BulletList>

              <SubHeading>Tech, AI & Development Pathway</SubHeading>
              <BodyText>For a candidate with technical aptitude, this is where the role becomes exceptional:</BodyText>
              <BulletList>
                <li>WordPress CMS: day-to-day content management, page builds, plugin management, SEO implementation</li>
                <li>React web: updating and contributing to our React-based frontend — components, UX improvements, A/B testing</li>
                <li>React Native mobile app: contributing to our guest-facing mobile application</li>
                <li>Node.js & PHP backend: understanding API integrations, booking flows, data connections</li>
                <li>AI tools & automation: using and helping integrate AI into marketing workflows and customer-facing features</li>
                <li>Analytics & data: building reports, querying data, making the numbers mean something</li>
              </BulletList>
            </Section>

            {/* Requirements */}
            <Section>
              <SectionTitle>What We Are Looking For</SectionTitle>

              <SubHeading>Essential</SubHeading>
              <BulletList>
                <li>Recent graduate in Marketing, Digital Media, Computer Science, or equivalent demonstrable experience</li>
                <li>A genuine passion for digital marketing with a creative eye for content and storytelling</li>
                <li>Strong written and verbal communication skills with confidence across different audiences</li>
                <li>Highly organised, detail-oriented, and able to juggle multiple priorities in a fast-paced environment</li>
                <li>A team player who can also work independently, take initiative, and see things through</li>
                <li>A full driving licence — the Estate is rural and getting around it matters</li>
                <li>Hands-on attitude — this is a high-energy, high-output role</li>
              </BulletList>

              <SubHeading>Highly Desirable</SubHeading>
              <BulletList>
                <li>Any coding experience — HTML, CSS, JavaScript, PHP, Python, SQL — even if self-taught or personal projects</li>
                <li>Familiarity with React, React Native, Node.js, or WordPress</li>
                <li>Experience with or strong interest in AI tools (ChatGPT, Claude, Midjourney, automation platforms)</li>
                <li>Paid digital advertising experience (Meta Ads, Google Ads) or willingness to train rapidly</li>
                <li>Experience with social media scheduling tools</li>
                <li>An analytical mindset — comfortable with data, dashboards, and drawing conclusions from numbers</li>
              </BulletList>
            </Section>

            {/* What you'll get */}
            <Section>
              <SectionTitle>What You Will Get</SectionTitle>
              <BulletList>
                <li>A paid graduate role with a competitive salary for the right candidate</li>
                <li>Real responsibility and real projects</li>
                <li>Mentoring from experienced marketing, digital, and development professionals</li>
                <li>Access to our full technology stack — and the opportunity to genuinely contribute to it</li>
                <li>A working environment unlike any other — a historic, living castle estate in the English countryside</li>
                <li>For the right person: a clear, genuine pathway to a permanent full-time role spanning marketing and tech</li>
              </BulletList>
            </Section>

            {/* Commercial areas */}
            <Section>
              <SectionTitle>Commercial Areas You Will Support</SectionTitle>
              <BulletList>
                <li>Belvoir Castle & The Belvoir Retail Village</li>
                <li>Retail & Estate Produce</li>
                <li>Holiday Accommodation</li>
                <li>Estate Events & Corporate Hospitality</li>
                <li>Belvoir Weddings</li>
                <li>Belvoir Wellness</li>
                <li>Belvoir Shoots</li>
              </BulletList>
            </Section>
          </div>

          {/* Right column — application form */}
          <div>
            <StickyCard ref={formRef}>
              {submitted ? (
                <>
                  <CardTitle>Application Received</CardTitle>
                  <StatusMessage type="success">
                    Thank you — your application has been submitted successfully. We will be in touch with you soon.
                  </StatusMessage>
                  <SmallNote style={{ marginTop: '1.25rem', opacity: 0.75 }}>
                    In the meantime, feel free to explore the rest of belvoircastle.com.
                  </SmallNote>
                </>
              ) : (
                <>
                  <CardTitle>Apply for This Role</CardTitle>

                  <form onSubmit={handleSubmit} noValidate>
                    <FormGroup>
                      <Label>First Name <span className="req">*</span></Label>
                      <Input
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        placeholder="Jane"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Last Name <span className="req">*</span></Label>
                      <Input
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        placeholder="Smith"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Email Address <span className="req">*</span></Label>
                      <Input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@example.com"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Phone Number</Label>
                      <Input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+44 7700 900000"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Portfolio / GitHub / Website</Label>
                      <Input
                        type="url"
                        name="portfolio"
                        value={form.portfolio}
                        onChange={handleChange}
                        placeholder="https://..."
                      />
                    </FormGroup>

                    <Divider />

                    <FormGroup>
                      <Label>CV <span className="req">*</span></Label>
                      <FileUploadArea hasFile={!!cvFile}>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange(setCvFile)}
                        />
                        <FileUploadText selected={!!cvFile}>
                          {cvFile ? cvFile.name : 'Choose file — CV'}
                        </FileUploadText>
                        {!cvFile && (
                          <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                        )}
                      </FileUploadArea>
                    </FormGroup>

                    <FormGroup>
                      <Label>Cover Letter <span className="req">*</span></Label>
                      <FileUploadArea hasFile={!!coverFile}>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange(setCoverFile)}
                        />
                        <FileUploadText selected={!!coverFile}>
                          {coverFile ? coverFile.name : 'Choose file — Cover Letter'}
                        </FileUploadText>
                        {!coverFile && (
                          <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                        )}
                      </FileUploadArea>
                    </FormGroup>

                    <Divider />

                    <FormGroup>
                      <Label>Cover Note <span className="req">*</span></Label>
                      <TextArea
                        name="coverNote"
                        value={form.coverNote}
                        onChange={handleChange}
                        placeholder="Tell us: what excites you about this role; what you've built or achieved digitally; and what draws you to the technology side — if applicable."
                        rows={5}
                        required
                      />
                      <SmallNote>Applications without a cover note will not be considered.</SmallNote>
                    </FormGroup>

                    {status && (
                      <StatusMessage type={status.type}>{status.message}</StatusMessage>
                    )}

                    <SubmitButton type="submit" disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit Application'}
                    </SubmitButton>

                    <SmallNote style={{ marginTop: '0.85rem' }}>
                      Your information is kept confidential and used only for this recruitment process.
                    </SmallNote>
                  </form>
                </>
              )}
            </StickyCard>
          </div>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default DigitalMarketingInternship;
